import _get from 'lodash-es/get';
import _flow from 'lodash/fp/flow';
import _join from 'lodash/fp/join';
import _map from 'lodash/fp/map';
import _upperFirst from 'lodash/fp/upperFirst';
import _words from 'lodash/fp/words';

import type { types } from '@bedc/types';

/**
 * Transform a byte size value into a readable size string.
 *
 * @param size The size in bytes.
 * @returns The readable size string.
 */
const toFileSizeString = (size: number) => {
	const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
	return (
		Number((size / Math.pow(1024, i)).toFixed(2)) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i]
	);
};

/**
 * Convert a string to title case.
 *
 * @param text The text to convert.
 * @returns The converted text.
 */
const toTitleCase = (text?: string | null) =>
	text
		?.replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()) // Initial char (after -/_)
		?.replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase()); // First char after each -/_

const toFixedDecimals = (value: number, decimals: number): number => {
	const factor = Math.pow(10, decimals);
	return Math.round((value + Number.EPSILON) * factor) / factor;
};

/**
 * Convert a camelCase identifier to snake_case.
 *
 *   camelToSnake('subFloor')                    => 'sub_floor'
 *   camelToSnake('wholeHouseFoundationRebar')   => 'whole_house_foundation_rebar'
 *   camelToSnake('already_snake')               => 'already_snake'
 *
 * Expects camelCase input (leading lowercase). PascalCase input
 * (`WholeHouse`) produces a leading underscore (`_whole_house`); if
 * that matters at a call site, strip leading underscores or
 * lower-case the first character first.
 *
 * Generic: a string-LITERAL argument returns the precise `SnakeCase<S>`
 * literal type; a plain `string` returns `string`. This lets callers derive
 * type-safe column names directly from `as const` id lists (used by the
 * Redshift source pipeline to keep the converter's `flags` SUPER keys aligned
 * with the flatten MV's projected snake_case columns — one shared impl).
 *
 * @param s camelCase string.
 * @returns snake_case equivalent.
 */
const camelToSnake = <S extends string>(s: S): types.string.SnakeCase<S> =>
	s.replace(/([A-Z])/g, '_$1').toLowerCase() as types.string.SnakeCase<S>;

/**
 * Convert a kebab-case identifier to snake_case.
 *
 *   kebabToSnake('design-due')      => 'design_due'
 *   kebabToSnake('already_snake')   => 'already_snake'
 *
 * Generic like `camelToSnake`: a literal argument returns the precise
 * `KebabToSnake<S>` literal; a plain `string` returns `string`.
 *
 * @param s kebab-case string.
 * @returns snake_case equivalent.
 */
const kebabToSnake = <S extends string>(s: S): types.string.KebabToSnake<S> =>
	s.replace(/-/g, '_') as types.string.KebabToSnake<S>;

// 'someCustom.path.that.you.want
// becomes
// 'Some Custom Path That You Want'
const jsonPathToLabel = _flow(_words, _map(_upperFirst), _join(' '));

type Dictionary = {
	[key: string]: string | Dictionary;
};

const formatLabel = (
	label: string,
	opts: {
		dictionary?: Dictionary;
		replacements?: Record<string, string | number>;
		fallback?: string;
	} = {}
): string => {
	const PLACEHOLDER_PATTERN = /([{]{1,2}([^}]+)[}]{1,2})/gm;

	/** Caps total recursion depth for long chains or misconfiguration. */
	const MAX_FORMAT_LABEL_DEPTH = 32;

	// Nested `{…}` in substitution values are expanded recursively; keys on the
	// current expansion chain are skipped so copy can contain literal `{sameKey}`.
	const resolve = (
		str: string,
		depth: number,
		expandingPlaceholderKeys: ReadonlySet<string>
	): string => {
		if (!str || depth > MAX_FORMAT_LABEL_DEPTH) {
			return str;
		}

		// first see if the label is a path to a value in the dictionary
		if (opts.dictionary) {
			const fromDictionary = _get(opts.dictionary, str);

			if (fromDictionary && typeof fromDictionary === 'string') {
				return resolve(fromDictionary, depth + 1, expandingPlaceholderKeys);
			}
		}

		const matches = [...str.matchAll(PLACEHOLDER_PATTERN)];
		if (matches.length === 0) {
			return str;
		}

		let formattedLabel = str;
		for (const match of matches) {
			const placeholderText = match[1] as string;
			const placeholderLabel = match[2] as string;
			const isExpandingKey = expandingPlaceholderKeys.has(placeholderLabel);
			let placeholderResolved = false;

			if (opts.dictionary && !isExpandingKey) {
				const fromDictionary = _get(opts.dictionary, placeholderLabel);

				if (fromDictionary && typeof fromDictionary === 'string') {
					const nextKeys = new Set([...expandingPlaceholderKeys, placeholderLabel]);
					const expanded = resolve(fromDictionary, depth + 1, nextKeys);
					formattedLabel = formattedLabel.replaceAll(placeholderText, expanded);
					placeholderResolved = true;
				}
			}

			if (opts.replacements && !isExpandingKey) {
				const fromReplacements = _get(opts.replacements, placeholderLabel);

				if (
					fromReplacements !== undefined &&
					fromReplacements !== null &&
					(typeof fromReplacements === 'string' || typeof fromReplacements === 'number')
				) {
					const nextKeys = new Set([...expandingPlaceholderKeys, placeholderLabel]);
					const expanded = resolve(String(fromReplacements), depth + 1, nextKeys);
					formattedLabel = formattedLabel.replaceAll(placeholderText, expanded);
					placeholderResolved = true;
				}
			}

			if (opts.fallback && !placeholderResolved && !isExpandingKey) {
				const expandedFallback = resolve(
					opts.fallback,
					depth + 1,
					expandingPlaceholderKeys
				);
				formattedLabel = formattedLabel.replaceAll(placeholderText, expandedFallback);
			}
		}

		return formattedLabel;
	};

	return resolve(label, 0, new Set());
};

export const formatUtils = {
	toFileSizeString,
	toTitleCase,
	toFixedDecimals,
	camelToSnake,
	kebabToSnake,
	jsonPathToLabel,
	formatLabel,
};
